// Floor plan core: room- and objective-appropriate flooring, shared by the
// Node demo and the browser preview. Covers refinishing existing wood
// (re-stain to any tone), engineered wood, LVP, laminate, tile, stone, and
// carpet. Pure: math + the seed catalog, reuses color.ts for effective
// temperature so a floor is judged under the room's recommended light.
//
// Flooring is a Level-0/2 anchor-grade decision: it often drives the whole
// palette (SPEC section 3.4). The Paint Engine's W-01 harmony check reads
// this floor's temperature; here we just choose and explain the floor.

import floorData from "../../data/seed/flooring.json" with { type: "json" };
import { DEFAULT_STYLE_PULL } from "./types.js";
import type { RoomType, Orientation, Objective, StyleKey } from "./types.js";
import { styleFit } from "./style.js";
import { effectiveTemperature } from "./color.js";

export interface Floor {
  category: "wood" | "lvp" | "laminate" | "tile" | "stone" | "carpet";
  name: string;
  stain: string;
  hex: string;
  lrv: number;
  temperature: number;
  undertone: string;
  chroma_opinion: number;
  texture: string;
  durability_class: string;
  water_resistant: boolean;
  rental_suitability: number;
  price_per_sqft: number;
  install: string;
  refinishable: boolean;
  rooms: RoomType[];
}

const FLOORS = (floorData as { floors: Floor[] }).floors;

export type FloorCategory = Floor["category"];

export interface FloorPlanInput {
  roomType: RoomType;
  orientation: Orientation;
  bulbCCT: number;
  objective: Objective;
  sqft: number;
  styleVibe?: StyleKey; // if set, steers the pick at stylePull weight
  stylePull?: number; // 0..1; how hard the vibe steers (default 0.25)
  // User-selectable factors, mirroring paintPlan. targetLrv steers toward a
  // floor lightness (dark walnut vs pale oak) rather than naming a product;
  // categoryFilter restricts the material family ("I want tile").
  targetLrv?: number;
  categoryFilter?: FloorCategory;
}

export interface FloorOption {
  floor: Floor;
  effTemp: number; // temperature under the room's light
  score: number;
}

export interface FloorPlan {
  top: Floor;
  effTemp: number;
  options: FloorOption[]; // room-appropriate, best first (for a picker)
  availableCategories: FloorCategory[]; // material families valid for this room
  filteredOut: number; // options removed by the category filter
  targetLrv: number | null; // the active LRV target, if the user set one
  reasoning: string[];
}

function wetRoom(roomType: RoomType): boolean {
  return roomType === "kitchen" || roomType === "bath" || roomType === "laundry";
}

// Floors that suit this room. Wet rooms require water resistance; rentals
// tighten that to kitchens too. Carpet is filtered out of wet and main
// living/dining space by its own `rooms` list already.
export function roomFloors(roomType: RoomType, objective: Objective): Floor[] {
  return FLOORS.filter((f) => {
    if (!f.rooms.includes(roomType)) return false;
    const mustResist =
      roomType === "bath" || roomType === "laundry" ||
      (roomType === "kitchen" && objective === "rental");
    if (mustResist && !f.water_resistant) return false;
    return true;
  });
}

// Absolute 0..1 principle score (a weighted average of 0..1 sub-scores), so
// the vibe blend in planFloor is a true share rather than a min-max artifact.
function scoreFloor(f: Floor, input: FloorPlanInput): number {
  const w = input.objective === "rental"
    ? { dur: 1.0, cost: 0.7, appeal: 0.5 }
    : input.objective === "flip"
    ? { dur: 0.6, cost: 0.8, appeal: 1.0 }
    : { dur: 0.6, cost: 0.4, appeal: 0.9 };
  const durability = f.rental_suitability; // 0..1
  const cost = Math.max(0, Math.min(1, 1 - f.price_per_sqft / 15)); // 0..1
  // Broadly appealing floors are mid-light warm neutrals with quiet grain.
  const appeal = Math.min(1,
    (f.lrv >= 35 && f.lrv <= 65 ? 0.5 : 0.2) +
    (1 - f.chroma_opinion) * 0.3 +
    (f.temperature >= 0 ? 0.2 : 0.1));
  let weighted = (durability * w.dur + cost * w.cost + appeal * w.appeal) / (w.dur + w.cost + w.appeal);
  // An explicit lightness target is a deliberate choice, so it dominates the
  // objective weighting (same stance as paintPlan's TARGET_LRV_WEIGHT).
  if (input.targetLrv !== undefined) {
    const proximity = Math.max(0, 1 - Math.abs(f.lrv - input.targetLrv) / 25);
    weighted = weighted * 0.35 + proximity * 0.65;
  }
  const water = wetRoom(input.roomType) && f.water_resistant ? 0.05 : 0;
  return Math.min(1, weighted + water);
}

export function planFloor(input: FloorPlanInput): FloorPlan {
  const roomPool = roomFloors(input.roomType, input.objective);
  const availableCategories = [...new Set(roomPool.map((f) => f.category))];

  // Material-family filter, if the user picked one. Ignore a filter that
  // would empty the field (e.g. carpet in a bath) rather than returning
  // nothing; the reasoning says so.
  const filtered = input.categoryFilter
    ? roomPool.filter((f) => f.category === input.categoryFilter)
    : roomPool;
  const filterApplied = input.categoryFilter !== undefined && filtered.length > 0;
  const pool = filterApplied ? filtered : roomPool;
  const filteredOut = filterApplied ? roomPool.length - pool.length : 0;

  // Absolute 0..1 principle score, then the vibe blended in at a capped
  // weight so stylePull is a true share (see paintPlan for the same pattern).
  const pull = input.styleVibe ? Math.max(0, Math.min(1, input.stylePull ?? DEFAULT_STYLE_PULL)) : 0;
  const hasTarget = input.targetLrv !== undefined;

  const options: FloorOption[] = pool
    .map((f) => {
      const effTemp = effectiveTemperature(f.temperature, input.orientation, input.bulbCCT);
      const principle = scoreFloor(f, input);
      // When the user pinned a lightness, the vibe stops steering lightness
      // and only speaks to temperature and grain (same as paintPlan).
      const fit = input.styleVibe
        ? styleFit({ name: f.name, lrv: f.lrv, temperature: effTemp, chroma: f.chroma_opinion }, input.styleVibe, "floor", hasTarget)
        : 0;
      return { floor: f, effTemp, score: principle * (1 - pull) + fit * pull };
    })
    .sort((a, b) => b.score - a.score);

  const top = options[0].floor;
  const reasoning: string[] = [];

  if (input.categoryFilter && !filterApplied) {
    reasoning.push(`No ${input.categoryFilter} option suits a ${input.roomType} here, so the filter was ignored and the full field is in play.`);
  } else if (filterApplied) {
    reasoning.push(`Limited to ${input.categoryFilter}: ${filteredOut} other option${filteredOut === 1 ? "" : "s"} set aside at your request.`);
  }
  if (hasTarget) {
    reasoning.push(`You asked for a floor near LRV ${input.targetLrv}; ${top.name} at LRV ${top.lrv} is the closest fit in that field.`);
  }

  if (wetRoom(input.roomType)) {
    reasoning.push(
      `For a ${input.roomType}, water resistance is the gate. ${top.name} is a ${top.category === "lvp" ? "waterproof LVP" : top.category} at $${top.price_per_sqft.toFixed(2)}/sqft, ${top.install} install, class-${top.durability_class} wear.`
    );
  } else {
    reasoning.push(
      `${top.name}: class-${top.durability_class} wear at $${top.price_per_sqft.toFixed(2)}/sqft, ${top.install} install, ${top.texture} texture.`
    );
  }

  const existingWood = pool.find((f) => f.category === "wood" && f.refinishable);
  if (existingWood) {
    reasoning.push(
      `If there is existing wood, refinishing usually beats replacement on cost and lets you re-stain to any tone. The stain, not the species, is the real color decision.`
    );
  }
  if (input.objective === "rental" && top.category === "lvp") {
    reasoning.push(
      `Rental math favors LVP here: waterproof, scratch-tough, and DIY-friendly for fast turns between tenants.`
    );
  }
  if (top.category === "carpet") {
    reasoning.push(
      `Carpet reads dated in main living areas but is still valued in bedrooms for warmth and sound. Kept it here for that reason only.`
    );
  }
  reasoning.push(
    `This floor's temperature (${top.temperature}, ${top.temperature > 0.12 ? "warm" : top.temperature < -0.12 ? "cool" : "neutral"}) is handed to the wall-color engine, which scores every candidate's W-01 harmony against it. The floor is resolved first because it anchors the palette.`
  );

  return {
    top, effTemp: options[0].effTemp, options,
    availableCategories, filteredOut, targetLrv: input.targetLrv ?? null,
    reasoning,
  };
}
